/* eslint-disable @typescript-eslint/no-explicit-any */
import { getAppEnv } from '@pbk-web/app-env';
import type { ClientUserGrouping, MinimalUser, TrainingEventUser } from '@pbk-web/types';
import { LanguageUtils, mouseAndKeyboardCallbackProps } from '@pbk-web/utils';
import type { FC, ReactNode, UIEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Item as SelectListItem } from '../menus/SelectListMenu';
import { SelectListMenu } from '../menus/SelectListMenu';
import GroupAvatar from './GroupAvatar';
import ProfileAvatar, { ImageSize } from './ProfileAvatar';
import type { AvatarRole } from './useAvatarRoleNames';
import useAvatarRoleNames from './useAvatarRoleNames';

type ProfileStackItem = MinimalUser & {
  role?: number;
  requiresAction?: boolean | null;
  formSectionId?: string | null;
  sortOrder?: number | null;
  memberType?: number;
  translations?: Record<string, any>;
  users?: MinimalUser[];
  icon?: ReactNode;
};

type ProfileImageStackProps = {
  users?: (ProfileStackItem | ClientUserGrouping | TrainingEventUser)[];
  size?: ImageSize;
  caption?: string;
  displayLimit?: number;
};

export { ImageSize };

const ProfileImageStack: FC<ProfileImageStackProps> = (props) => {
  const { users: items = [], size = ImageSize.S, caption, displayLimit = 3 } = props;
  const currentUserId = getAppEnv().useCurrentUser((x: { value: { id?: string } | null }) => x.value?.id);
  const { t } = useTranslation(['common']);
  const [showMore, setShowMore] = useState(false);

  let moreSizeCss = 'w-12 h-12 text-pbk-14';
  switch (size) {
    case ImageSize.XXS:
      moreSizeCss = 'min-w-5 w-5 h-5 text-[10px]';
      break;
    case ImageSize.XS:
      moreSizeCss = 'min-w-8 w-8 h-8 text-pbk-12';
      break;
    case ImageSize.S:
      moreSizeCss = 'min-w-10 w-10 h-10 text-pbk-12';
      break;
    default:
      moreSizeCss = 'min-w-12 w-12 h-12 text-pbk-14';
      break;
  }

  const groupedItems = useMemo(() => {
    const itemMap = new Map<
      string,
      { id: string; name: string } & ({ user: ProfileStackItem; roles: AvatarRole | AvatarRole[] } | { group: ClientUserGrouping })
    >();

    (items as any[]).forEach((item: any) => {
      if (!item?.id) return;

      if (!itemMap.has(item.id)) {
        if ('translations' in item) {
          const group = item as ClientUserGrouping;
          itemMap.set(item.id, { id: item.id, group, name: LanguageUtils.getTranslation('name', group.translations ?? {}) ?? '' });
        } else if ('memberType' in item) {
          itemMap.set(item.id, {
            id: item.id,
            user: item,
            roles: { role: item.memberType, requiresAction: false, roleOrder: null, isStepRole: false },
            name: `${item.firstName} ${item.lastName}`.trim() || item.email || '',
          });
        } else {
          itemMap.set(item.id, { id: item.id, user: item, roles: [], name: `${item.firstName} ${item.lastName}`.trim() || item.email || '' });
        }
      }

      const itemData = itemMap.get(item.id);
      if (itemData && 'roles' in itemData && Array.isArray(itemData.roles)) {
        const existingRoleIndex = (itemData.roles as AvatarRole[]).findIndex(
          (r) => r.role === item.role && r.requiresAction === !!item.requiresAction,
        );
        if (existingRoleIndex === -1 && item.role !== null && item.role !== undefined) {
          (itemData.roles as AvatarRole[]).push({
            role: item.role,
            requiresAction: !!item.requiresAction,
            roleOrder: item.sortOrder,
            isStepRole: !!item.formSectionId,
          });
        }
      }
    });

    return Array.from(itemMap.values()).sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [items, currentUserId]);

  const itemsToDisplay = groupedItems.slice(0, displayLimit);
  const remainingItems = groupedItems.slice(displayLimit);

  const RemainingUsersListRenderer = useMemo(() => {
    return function ListRenderer(props: SelectListItem) {
      const itemIndex = remainingItems.findIndex((x) => x.id === props.id);
      const remItem = remainingItems?.[itemIndex];

      const roleNames = useAvatarRoleNames('roles' in remItem ? (remItem?.roles ?? []) : [], t);

      if (!remItem) return null;

      return (
        <div
          key={remItem.id}
          className={`flex items-center p-1 ${itemIndex === remainingItems.length - 1 ? 'border-b-0' : 'border-b'} border-greyscale-5`}
        >
          <div className="px-1">
            {'user' in remItem ? (
              <ProfileAvatar
                data-testid={`user-${remItem.id}`}
                user={remItem.user as MinimalUser}
                roles={remItem.roles}
                size={size}
                isCurrentUser={remItem.id === currentUserId}
                icon={(remItem.user as ProfileStackItem).icon}
              />
            ) : (
              <GroupAvatar data-testid={`tag-${remItem.id}`} users={remItem.group.users as MinimalUser[]} size={size} currentUserId={currentUserId} />
            )}
          </div>
          <div className="px-1">
            <span className="block">{remItem.name}</span>
            <span className="text-pbk-12 text-text-2">{roleNames}</span>
          </div>
        </div>
      );
    };
  }, [remainingItems, size, t, currentUserId]);

  return (
    <div className="flex h-full" data-testid="profile-stack">
      <div className="flex flex-col self-center">
        <span className={`text-pbk-12 text-center font-medium ${caption ? 'mb-3' : ''}`} data-testid="caption" aria-label={t('aria-label.caption')}>
          {caption}
        </span>
        <div className="profile-image-stack relative flex justify-items-center self-center">
          {groupedItems.length === 0 && <ProfileAvatar data-testid="no-users" data-user-count={0} size={size} />}
          {itemsToDisplay.map((item, index) => {
            if ('group' in item) {
              return (
                <GroupAvatar
                  key={item.id}
                  data-testid={`tag-${item.id}`}
                  data-user-count={groupedItems.length}
                  name={item.name}
                  users={item.group.users as MinimalUser[]}
                  withMargin={index > 0 && groupedItems.length > 1}
                  size={size}
                  currentUserId={currentUserId}
                />
              );
            }

            return (
              <div key={item.id} className="relative">
                <ProfileAvatar
                  data-testid={`user-${item.id}`}
                  data-user-count={groupedItems.length}
                  user={item.user as MinimalUser}
                  roles={item.roles}
                  size={size}
                  withMargin={index > 0 && groupedItems.length > 1}
                  isCurrentUser={item.id === currentUserId}
                  icon={(item.user as ProfileStackItem).icon}
                />
              </div>
            );
          })}
          {remainingItems.length > 0 && (
            <SelectListMenu
              isOpen={showMore}
              options={remainingItems.map((item) => ({ id: item.id || '', value: item.name, text: item.name }))}
              customListItemRenderer={RemainingUsersListRenderer}
              onBlur={() => setShowMore(false)}
              className="border-none text-left"
              width="w-fit"
            >
              {(triggerProps) => (
                <div {...triggerProps} className="relative">
                  <div
                    {...mouseAndKeyboardCallbackProps((e: UIEvent<HTMLElement>) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      setShowMore(!showMore);
                    })}
                    className={`bg-greyscale-5 relative -mt-px -ml-2 inline-flex items-center justify-center rounded-full border-2 border-white p-1 pt-[5px] font-medium text-black ${moreSizeCss} cursor-pointer`}
                  >
                    {remainingItems.length > 99 ? '99+' : `+${remainingItems.length}`}
                  </div>
                </div>
              )}
            </SelectListMenu>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileImageStack;
